import { useQuery } from "@tanstack/react-query";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";

// API's
import { getModelYears } from "@/Common/api/vehicles";

// Helpers
import { getRangeYears } from "@/Common/helpers/Requester";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// MUI
import { FormControl, FormControlLabel, Paper, Radio, RadioGroup, Stack, Typography } from "@mui/material";

// Icons
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";

const getRelativeYearsRange = (start = -5, end = 2) =>
	Array.from({ length: Math.abs(end) + Math.abs(start) + 1 }, (_, i) => {
		const year = start + i;
		const sign = year > 0 ? "+" : "";
		let label;
		if ([-1, 0, 1].includes(year)) {
			label = `${sign}${year} Year`;
		} else {
			label = `${sign}${year} Years`;
		}
		return { label, year };
	}).reverse();

const RELATIVE_YEARS_RANGE = getRelativeYearsRange();

const getSelectedYear = (year) => (isNil(year) ? null : RELATIVE_YEARS_RANGE.find((item) => item.year === year));

function useYearsByMakeAndModel(make = "", model = "", isFixed = false) {
	const { data, isLoading } = useQuery({
		queryKey: ["modelYears", make, model],
		queryFn: async () => getModelYears(make, model),
		staleTime: Infinity,
		enabled: !!make && !!model && isFixed,
	});

	return {
		years: data || [],
		isLoading,
	};
}

function getYears(years = [], left = 0) {
	if (!years?.length || !left) {
		return [];
	}
	return years.filter((element) => element?.year >= left);
}

const toSortedYears = (years = []) => years.slice().sort((left, right) => (Number(left.year) < Number(right.year) ? -1 : 1));

function includeMissingYears(years = [], left = null, right = null) {
	if (!years?.length) {
		return [];
	}

	if (left) {
		years = years.filter((item) => item.year !== left);
		years.push({ year: left });
	}
	if (right) {
		years = years.filter((item) => item.year !== right);
		years.push({ year: right });
	}

	return toSortedYears(years);
}

function SelectOptionsContent(props) {
	return <SmartSelector {...props} selectBy={"year"} />;
}
const SelectOptions = memo(SelectOptionsContent);

export default function AgeOptions(props) {
	const {
		make = "",
		model = "",
		rangeType = "RELATIVE",
		left,
		right,
		sxLeft = null,
		sxRight = null,
		disabled = false,
		error = false,
		additionalRadio = null,
		additionalOption = null,
		useSwitch = false,
		sxSwitch = null,
		sx = null,
		useNoModelYears = false,
		clearable = false,
		relativeTitle = "Current",
		sxRelativeSeparator = { padding: "0 10px" },
		onActionUpdate = () => {},
	} = props;

	const [innerModel, setInnerModel] = useState({
		left: null,
		right: null,
		rangeType: "RELATIVE",
	});

	const isFixedOption = innerModel?.rangeType === "FIXED";
	const { years, isLoading } = useYearsByMakeAndModel(make, model, isFixedOption);

	const propagate = useCallback(
		(updatedModel) => {
			onActionUpdate?.(updatedModel);
		},
		[onActionUpdate]
	);

	const handleChangeRange = (_, value) => {
		const currentModel = { ...innerModel, left: null, right: null, rangeType: value };
		setInnerModel(currentModel);
		propagate(currentModel);
	};

	const handleChangeYear = useCallback(
		(selected, index) => {
			const currentModel = { ...innerModel };
			currentModel[index] = selected?.year;

			if (innerModel.rangeType === "FIXED" && index === "left" && currentModel.right < currentModel.left) {
				currentModel.right = null;
			}

			setInnerModel(currentModel);
			propagate(currentModel);
		},
		[innerModel, propagate]
	);

	const fixedYears = useMemo(() => {
		if (Boolean(useNoModelYears) && !years?.length) {
			return getRangeYears();
		}
		return years;
	}, [years, useNoModelYears]);

	useEffect(() => {
		setInnerModel({
			left,
			right,
			rangeType,
		});

		return () => {
			setInnerModel({
				left: null,
				right: null,
				rangeType: "RELATIVE",
			});
		};
	}, [left, right, rangeType]);

	const OptionModel = useMemo(() => {
		switch (innerModel.rangeType) {
			case "RELATIVE":
				return (
					<Fragment>
						<Stack direction="column" justifyContent="center" alignItems="center" sx={sxLeft || null}>
							<SelectOptions
								showBy={"label"}
								options={RELATIVE_YEARS_RANGE}
								selected={getSelectedYear(innerModel.left)}
								disabledOptions={
									isNil(innerModel.right)
										? []
										: RELATIVE_YEARS_RANGE.filter(({ year }) => year > innerModel.right).map(({ year }) => year)
								}
								inputProps={{ placeholder: "Start" }}
								clearable={clearable}
								disabled={disabled}
								error={error}
								onChangeAction={(selected) => handleChangeYear(selected, "left")}
							/>
						</Stack>

						<Stack sx={sxRelativeSeparator}>
							<Typography className="!font-sans !text-[14.5px] text-default" sx={{ textAlign: "center" }}>
								{relativeTitle}
							</Typography>
						</Stack>

						<Stack direction="column" justifyContent="center" alignItems="center" sx={sxRight || null}>
							<SelectOptions
								showBy={"label"}
								options={RELATIVE_YEARS_RANGE}
								selected={getSelectedYear(innerModel.right)}
								disabledOptions={
									isNil(innerModel.left)
										? []
										: RELATIVE_YEARS_RANGE.filter(({ year }) => year < innerModel.left).map(({ year }) => year)
								}
								inputProps={{ placeholder: "End" }}
								clearable={clearable}
								disabled={disabled}
								error={error}
								onChangeAction={(selected) => handleChangeYear(selected, "right")}
							/>
						</Stack>
					</Fragment>
				);
			case "FIXED": {
				const yearsToUse = includeMissingYears(fixedYears, innerModel.left, innerModel.right);
				return (
					<Fragment>
						<Stack direction="column" justifyContent="center" alignItems="center" sx={sxLeft}>
							<SelectOptions
								showBy={"year"}
								options={yearsToUse}
								selected={innerModel.left ? { year: innerModel.left } : null}
								inputProps={{ placeholder: "Start" }}
								clearable={clearable}
								disabled={disabled}
								error={error}
								loading={isLoading}
								onChangeAction={(selected) => handleChangeYear(selected, "left")}
							/>
						</Stack>

						<Stack sx={{ width: "25px", paddingLeft: "6px" }}>
							<RemoveOutlinedIcon className="small-icon" />
						</Stack>

						<Stack direction="column" justifyContent="center" alignItems="center" sx={sxRight}>
							<SelectOptions
								showBy={"year"}
								options={getYears(yearsToUse, innerModel.left)}
								selected={innerModel.right ? { year: innerModel.right } : null}
								inputProps={{ placeholder: "End" }}
								clearable={clearable}
								disabled={disabled}
								error={error}
								loading={isLoading}
								onChangeAction={(selected) => handleChangeYear(selected, "right")}
							/>
						</Stack>
					</Fragment>
				);
			}
			default:
				return <Fragment>{Boolean(additionalOption) && additionalOption}</Fragment>;
		}
	}, [
		innerModel,
		disabled,
		error,
		clearable,
		relativeTitle,
		sxRelativeSeparator,
		fixedYears,
		isLoading,
		additionalOption,
		sxLeft,
		sxRight,
		handleChangeYear,
	]);

	useEffect(() => {
		setInnerModel({
			left,
			right,
			rangeType,
		});

		return () => {
			setInnerModel({
				left: 0,
				right: 0,
				rangeType: "RELATIVE",
			});
		};
	}, [rangeType, left, right, make, model]);

	return (
		<Paper elevation={0} sx={sx} style={{ backgroundColor: "transparent", width: "100%" }}>
			<Stack>
				{useSwitch && (
					<Stack sx={sxSwitch}>
						<FormControl>
							<RadioGroup
								row
								aria-labelledby="range-buttons-group"
								value={innerModel?.rangeType || "RELATIVE"}
								name="range-buttons-group"
								className={"radio-buttons-small regular-blue-color"}
								onChange={handleChangeRange}
							>
								<FormControlLabel
									disabled={disabled}
									value="RELATIVE"
									control={
										<Radio
											sx={{
												"& .MuiSvgIcon-root": {
													fontSize: 20,
												},
											}}
										/>
									}
									label="Relative range"
								/>
								<FormControlLabel
									disabled={disabled || !model}
									value="FIXED"
									control={
										<Radio
											sx={{
												"& .MuiSvgIcon-root": {
													fontSize: 20,
												},
											}}
										/>
									}
									label="Fixed Range"
								/>
								{Boolean(additionalRadio) && additionalRadio}
							</RadioGroup>
						</FormControl>
					</Stack>
				)}
			</Stack>

			<Stack direction="row" justifyContent="flex-start" alignItems="center">
				{OptionModel}
			</Stack>
		</Paper>
	);
}
AgeOptions.propTypes = {
	make: PropTypes.string,
	model: PropTypes.string,
	rangeType: PropTypes.string,
	left: PropTypes.number,
	right: PropTypes.number,
	sxLeft: PropTypes.object,
	sxRight: PropTypes.object,
	disabled: PropTypes.bool,
	error: PropTypes.bool,
	additionalRadio: PropTypes.element,
	additionalOption: PropTypes.element,
	useSwitch: PropTypes.bool,
	sxSwitch: PropTypes.object,
	sx: PropTypes.object,
	useNoModelYears: PropTypes.bool,
	clearable: PropTypes.bool,
	relativeTitle: PropTypes.string,
	sxRelativeSeparator: PropTypes.object,
	onActionUpdate: PropTypes.func,
};
